import { type GraphQLResult } from './apiClient';
import { LicenseListResponse, LicenseInput, ToolENUM } from '../types/license';
import { authService } from './authService';
import { getApiClient } from './apiClient';

const getLicensesQuery = /* GraphQL */ `
  query GetLicenses($email: String, $serialNo: String, $limit: Int, $page: Int, $searchTerm: String, $status: String, $model: ToolENUM) {
    getLicenses(email: $email, serialNo: $serialNo, limit: $limit, page: $page, searchTerm: $searchTerm, status: $status, model: $model) {
      licenses {
        email
        durationInDay
        dateStart
        dateEnd
        serialNo
        transactionDate
        isDisabled
        type
        model
      }
      total
      currentPage
      totalPages
    }
  }
`;

const createLicenseMutation = /* GraphQL */ `
  mutation CreateNewLicense($license: LicenseInput!) {
    createNewLicense(license: $license) {
      result
      message
      remainingDays
    }
  }
`;

const disableLicenseMutation = /* GraphQL */ `
  mutation DisableLicense($serialNo: String!, $email: String!, $isDisabled: Boolean!, $model: ToolENUM!) {
    disableLicense(serialNo: $serialNo, email: $email, isDisabled: $isDisabled, model: $model) {
      result
      message
    }
  }
`;

const extendLicenseMutation = /* GraphQL */ `
  mutation ExtendLicense($license: LicenseInput!) {
    extendLicense(license: $license) {
      result
      message
    }
  }
`;

const getLicenseRequestsQuery = /* GraphQL */ `
  query GetLicenseRequests($limit: Int, $page: Int, $searchTerm: String) {
    getLicenseRequests(limit: $limit, page: $page, searchTerm: $searchTerm) {
      requests {
        email
        serialNo
        requestDate
        uniqueRequestDay
        status
        activeBy
        tool
      }
      total
      currentPage
      totalPages
    }
  }
`;

const requestLicenseMutation = /* GraphQL */ `
  mutation RequestLicense($email: String!, $serialNo: String!, $tool: ToolENUM!) {
    requestLicense(email: $email, serialNo: $serialNo, tool: $tool) {
      result
      message
    }
  }
`;

const deleteLicenseRequestMutation = /* GraphQL */ `
  mutation DeleteLicenseRequest($email: String!, $serialNo: String!, $uniqueRequestDay: String!, $tool: ToolENUM!, $activeBy: String!, $status: String!) {
    deleteLicenseRequest(email: $email, serialNo: $serialNo, uniqueRequestDay: $uniqueRequestDay, tool: $tool, activeBy: $activeBy, status: $status)
  }
`;

const getLicensesBySerialNumbersQuery = /* GraphQL */ `
  query GetLicensesBySerialNumbers($serialNumbers: [String]!, $model: String!, $email: String) {
    getLicensesBySerialNumbers(serialNumbers: $serialNumbers, model: $model, email: $email) {
      licenses {
        email
        serialNo
        model
        remainingDays
        dateEnd
        dateStart
        durationInDay
        type
        isNew
      }
    }
  }
`;

const batchActivateLicensesMutation = /* GraphQL */ `
  mutation BatchActivateLicenses($licenses: [LicenseInput!]!) {
    batchActivateLicenses(licenses: $licenses) {
      result
      message
    }
  }
`;

const transferLicenseDaysMutation = /* GraphQL */ `
  mutation TransferLicenseDays($sourceEmail: String!, $sourceSerialNo: String!, $destEmail: String!, $destSerialNo: String!, $model: ToolENUM!) {
    transferLicenseDays(sourceEmail: $sourceEmail, sourceSerialNo: $sourceSerialNo, destEmail: $destEmail, destSerialNo: $destSerialNo, model: $model) {
      result
      message
    }
  }
`;

interface GetLicensesResponse {
  getLicenses: LicenseListResponse;
}

interface MutationResponse {
  result: boolean;
  message: string;
  remainingDays: number;
}

interface CreateLicenseResponse {
  createNewLicense: MutationResponse;
}

interface DisableLicenseResponse {
  disableLicense: MutationResponse;
}

interface ExtendLicenseResponse {
  extendLicense: MutationResponse;
}

interface GetLicenseRequestsResponse {
  getLicenseRequests: {
    requests: Array<{
      email: string;
      serialNo: string;
      requestDate: number;
      uniqueRequestDay: string;
      status: string;
      activeBy?: string;
      tool?: ToolENUM;
    }>;
    total: number;
    currentPage: number;
    totalPages: number;
  };
}

interface RequestLicenseResponse {
  requestLicense: {
    result: boolean;
    message: string;
  };
}

interface DeleteLicenseRequestResponse {
  deleteLicenseRequest: boolean;
}

interface GetLicensesBySerialNumbersResponse {
  getLicensesBySerialNumbers: {
    licenses: Array<{
      email: string;
      serialNo: string;
      model: string;
      remainingDays: number;
      dateEnd: number | null;
      dateStart: number | null;
      durationInDay: number | null;
      type: string | null;
      isNew: boolean;
    }>;
  };
}

interface BatchActivateLicensesResponse {
  batchActivateLicenses: {
    result: boolean;
    message: string;
  };
}

interface TransferLicenseDaysResponse {
  transferLicenseDays: {
    result: boolean;
    message: string;
  };
}

export const licenseService = {
  async checkAuth() {
    if (!authService.isAuthenticated()) {
      throw new Error('No authenticated user');
    }
    const groups = authService.getGroups();
    if (!groups.includes('Admin')) {
      throw new Error('User does not have Admin privileges');
    }
  },

  async checkAuthForRead() {
    if (!authService.isAuthenticated()) {
      throw new Error('No authenticated user');
    }
    const groups = authService.getGroups();
    if (!groups.includes('Admin') && !groups.includes('UserManager')) {
      throw new Error('User does not have Admin or UserManager privileges');
    }
  },

  async getLicenses(params: {
    email?: string;
    serialNo?: string;
    limit?: number;
    page?: number;
    searchTerm?: string;
    status?: string;
    model?: ToolENUM;
  }): Promise<LicenseListResponse> {
    try {
      await this.checkAuthForRead();

      // Clean up params before sending
      const variables = {
        email: params.email,
        serialNo: params.serialNo,
        limit: params.limit,
        page: params.page || 0,
        searchTerm: params.searchTerm,
        status: params.status,
        model: params.model
      };

      // Remove undefined values
      const cleanVariables = Object.fromEntries(
        Object.entries(variables).filter(([_, value]) => value !== undefined)
      );

      const response = await getApiClient().graphql({
        query: getLicensesQuery,
        variables: cleanVariables,
        authMode: 'userPool'
      }) as GraphQLResult<GetLicensesResponse>;

      if (response.errors) {
        console.error('GraphQL Errors:', response.errors);
        throw new Error(response.errors.map(e => e.message).join(', '));
      }

      if (!response.data) throw new Error('No data returned');

      return response.data.getLicenses;
    } catch (error) {
      console.error('Error fetching licenses:', error);
      throw error;
    }
  },

  async createLicense(license: LicenseInput): Promise<MutationResponse> {
    try {
      await this.checkAuth();

      const response = await getApiClient().graphql({
        query: createLicenseMutation,
        variables: { license },
        authMode: 'userPool'
      }) as GraphQLResult<CreateLicenseResponse>;

      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }

      if (!response.data) throw new Error('No data returned');
      return response.data.createNewLicense;
    } catch (error) {
      console.error('Error creating license:', error);
      throw error;
    }
  },

  async updateLicenseStatus(
    serialNo: string,
    email: string,
    isDisabled: boolean,
    model: ToolENUM
  ): Promise<MutationResponse> {
    try {
      await this.checkAuth();

      const response = await getApiClient().graphql({
        query: disableLicenseMutation,
        variables: {
          serialNo,
          email,
          isDisabled,
          model
        },
        authMode: 'userPool'
      }) as GraphQLResult<DisableLicenseResponse>;

      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }

      if (!response.data) throw new Error('No data returned');
      return response.data.disableLicense;
    } catch (error) {
      console.error('Error updating license status:', error);
      throw error;
    }
  },

  async extendLicense(license: LicenseInput): Promise<MutationResponse> {
    try {
      await this.checkAuth();
      const response = await getApiClient().graphql({
        query: extendLicenseMutation,
        variables: { license },
        authMode: 'userPool'
      }) as GraphQLResult<ExtendLicenseResponse>;

      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }

      if (!response.data) throw new Error('No data returned');
      return response.data.extendLicense;
    } catch (error) {
      console.error('Error extending license:', error);
      throw error;
    }
  },

  async getLicenseRequests(params: { limit: number; page: number; searchTerm?: string }) {
    try {
      const response = await getApiClient().graphql({
        query: getLicenseRequestsQuery,
        variables: {
          limit: params.limit,
          page: params.page,
          searchTerm: params.searchTerm || ''
        }
      }) as GraphQLResult<GetLicenseRequestsResponse>;

      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }

      if (!response.data) throw new Error('No data returned');
      return response.data.getLicenseRequests;
    } catch (error) {
      console.error('Error in getLicenseRequests:', error);
      throw error;
    }
  },

  async requestLicense(email: string, serialNo: string, uniqueRequestDay: string, tool: ToolENUM) {
    try {
      const response = await getApiClient().graphql({
        query: requestLicenseMutation,
        variables: {
          email,
          serialNo,
          tool
        }
      }) as GraphQLResult<RequestLicenseResponse>;

      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }

      if (!response.data) throw new Error('No data returned');
      return response.data.requestLicense;
    } catch (error) {
      console.error('Error in requestLicense:', error);
      throw error;
    }
  },

  async deleteLicenseRequest(email: string, serialNo: string, uniqueRequestDay: string, tool: ToolENUM, status: string) {
    try {
      const activeBy = authService.getEmail();

      if (!activeBy) {
        throw new Error('No authenticated user email found');
      }

      const response = await getApiClient().graphql({
        query: deleteLicenseRequestMutation,
        variables: {
          email,
          serialNo,
          uniqueRequestDay,
          tool,
          activeBy,
          status
        }
      }) as GraphQLResult<DeleteLicenseRequestResponse>;

      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }

      if (!response.data) throw new Error('No data returned');
      return { result: response.data.deleteLicenseRequest, message: response.data.deleteLicenseRequest ? 'License request deleted successfully' : 'License request not found or already deleted' };
    } catch (error) {
      console.error('Error in deleteLicenseRequest:', error);
      throw error;
    }
  },

  async getLicensesBySerialNumbers(serialNumbers: string[], model: string, email?: string) {
    try {
      const response = await getApiClient().graphql({
        query: getLicensesBySerialNumbersQuery,
        variables: {
          serialNumbers,
          model,
          email
        }
      }) as GraphQLResult<GetLicensesBySerialNumbersResponse>;

      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }

      if (!response.data) throw new Error('No data returned');
      return response.data.getLicensesBySerialNumbers;
    } catch (error) {
      console.error('Error in getLicensesBySerialNumbers:', error);
      throw error;
    }
  },

  async batchActivateLicenses(licenses: LicenseInput[]): Promise<{ result: boolean; message: string }> {
    try {
      await this.checkAuth();
      const response = await getApiClient().graphql({
        query: batchActivateLicensesMutation,
        variables: { licenses },
        authMode: 'userPool'
      }) as GraphQLResult<BatchActivateLicensesResponse>;

      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }

      if (!response.data) throw new Error('No data returned');
      return response.data.batchActivateLicenses;
    } catch (error) {
      console.error('Error activating licenses:', error);
      throw error;
    }
  },

  async transferLicenseDays(
    sourceEmail: string,
    sourceSerialNo: string,
    destEmail: string,
    destSerialNo: string,
    model: ToolENUM
  ): Promise<{ result: boolean; message: string }> {
    try {
      await this.checkAuth();
      const response = await getApiClient().graphql({
        query: transferLicenseDaysMutation,
        variables: {
          sourceEmail,
          sourceSerialNo,
          destEmail,
          destSerialNo,
          model
        },
        authMode: 'userPool'
      }) as GraphQLResult<TransferLicenseDaysResponse>;

      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }

      if (!response.data) throw new Error('No data returned');
      return response.data.transferLicenseDays;
    } catch (error) {
      console.error('Error transferring license days:', error);
      throw error;
    }
  }
};